import { AlertCircle, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export type InlineMessageTone = "error" | "warning" | "success" | "info";

export interface InlineMessageProps {
  children: ReactNode;
  title?: string;
  tone?: InlineMessageTone;
  className?: string;
}

const toneClasses: Record<InlineMessageTone, string> = {
  error: "border-status-error/25 bg-status-error-bg text-primary",
  warning: "border-status-warn/25 bg-status-warn-bg text-primary",
  success: "border-status-ok/25 bg-status-ok-bg text-primary",
  info: "border-border bg-surface-elevated text-primary",
};

const iconClasses: Record<InlineMessageTone, string> = {
  error: "text-status-error-text",
  warning: "text-status-warn-text",
  success: "text-status-ok-text",
  info: "text-muted",
};

const icons = {
  error: AlertCircle,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

export function InlineMessage({
  children,
  title,
  tone = "info",
  className,
}: InlineMessageProps) {
  const Icon = icons[tone];

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm",
        toneClasses[tone],
        className,
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", iconClasses[tone])} aria-hidden />
      <div className="min-w-0">
        {title ? <p className="font-medium">{title}</p> : null}
        <div className={cn("text-secondary", title && "mt-0.5")}>{children}</div>
      </div>
    </div>
  );
}